import { MouseEventHandler, useEffect, useState } from 'react';
import { Template } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Modal, ModalProps, SecondaryButton } from '@frontend/design-system';
import { SETTINGS_MODAL_INTERACTION_DELAY } from '../../constants';
import { SendInThreadModalContents } from '../components/send-in-thread/send-in-thread-modal-contents';
import type { OnThreadChangeFn, SendInThreadTemplateData, ThreadAttachedAsset } from '../types';

type BaseProps = Omit<ModalProps, 'children'> & {
  groupId: string;
  initThreadId?: string;
  onBack: MouseEventHandler<HTMLButtonElement>;
  openTemplateSelector?: () => void;
  selectedTemplate?: Template;
  secondaryButtonProps?: Parameters<typeof SecondaryButton>[0];
  onThreadChange?: OnThreadChangeFn;
  hideSelectTemplateButton?: boolean;
  onSend?: () => void;
  /** Additional attached assets (e.g. user-selected documents) rendered alongside the context preview asset. */
  attachedAssets?: ThreadAttachedAsset[];
};
type RecipientData =
  | {
      personId?: string;
      initPersonPhone: string;
      petId?: string;
    }
  | {
      personId: string;
      initPersonPhone?: string;
      petId?: string;
    };

export type SendInThreadModalProps = BaseProps & RecipientData & SendInThreadTemplateData;

/**
 * A modal wrapper for the `SendInThreadModalContents` component.
 * See [SendInThreadModalContents component](../components/send-in-thread/send-in-thread-modal-contents) for more details on props.
 */
export const SendInThreadModal = ({
  groupId,
  initThreadId,
  initPersonPhone,
  personId,
  onBack,
  secondaryButtonProps,
  templateType,
  contextPreviewProps,
  linkData,
  onThreadChange,
  openTemplateSelector,
  selectedTemplate,
  initialBody,
  initialMediaIds,
  hideSelectTemplateButton,
  propertyBindings,
  onSend,
  attachedAssets,

  ...modalProps
}: SendInThreadModalProps) => {
  const [temporarilyHideModal, setTemporarilyHideModal] = useState(false);
  const { isOpen } = useSettingsNavigate();
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  useEffect(() => {
    if (!isOpen && temporarilyHideModal) {
      setTimeout(() => {
        setTemporarilyHideModal(false);
      }, SETTINGS_MODAL_INTERACTION_DELAY);
    }
  }, [isOpen]);

  return (
    <>
      <Modal.Responsive
        minWidth={800}
        maxWidth={800}
        css={{
          maxHeight: 'calc(100vh - 44px)',
          padding: theme.spacing(3),
          ...(isSmallMode && {
            paddingTop: theme.spacing(2),
          }),
        }}
        {...modalProps}
        show={!temporarilyHideModal && modalProps.show}
      >
        <SendInThreadModalContents
          groupId={groupId}
          initThreadId={initThreadId}
          initPersonPhone={initPersonPhone}
          personId={personId ?? ''}
          onBack={onBack}
          secondaryButtonProps={secondaryButtonProps}
          templateType={templateType}
          contextPreviewProps={contextPreviewProps}
          linkData={linkData}
          onThreadChange={onThreadChange}
          initialBody={initialBody}
          initialMediaIds={initialMediaIds}
          hideSelectTemplateButton={hideSelectTemplateButton}
          onClose={modalProps.onClose}
          openTemplateSelector={openTemplateSelector}
          selectedTemplate={selectedTemplate}
          onSend={() => {
            onSend?.();
            modalProps.onClose();
          }}
          onSchedule={() => {
            modalProps.onClose();
          }}
          onOpenSettings={() => {
            setTemporarilyHideModal(true);
          }}
          propertyBindings={propertyBindings}
          attachedAssets={attachedAssets}
        />
      </Modal.Responsive>
    </>
  );
};
